import React, { ReactNode, useEffect, useState } from 'react'

import cx from 'classnames'
import { useRouter } from 'next/router'

import { Arrow } from './Icons/Arrow'

import styles from './Collapsible.module.scss'

export interface CollapsibleItem {
  title: string
  content: string | JSX.Element | ReactNode
}

interface CollapsibleProps {
  items: CollapsibleItem[]
  className?: string | string[]
  alignLeft?: boolean
  questionClassName?: string | string[]
  answerClassName?: string | string[]
  useQueryParams?: boolean
}

export const Collapsible: React.FC<CollapsibleProps> = ({
  items,
  className: additionalClasses,
  alignLeft,
  questionClassName,
  answerClassName,
  useQueryParams,
}) => {
  const router = useRouter()
  const [showItem, setShowItem] = useState<number | null>(null)

  useEffect(() => {
    setShowItem(null)
  }, [items])

  useEffect(() => {
    if (useQueryParams) {
      const id = router.query.collapsibleId
      setShowItem(id ? Number(id) : null)
    }
  }, [useQueryParams, router.query.collapsibleId])

  const toggleItemHandler = (
    event: React.MouseEvent<HTMLButtonElement> | React.MouseEvent<HTMLDivElement>,
    id: number,
  ) => {
    const target = event.target as HTMLElement
    if (target.nodeName === 'A') {
      return
    }

    if (useQueryParams) {
      const { collapsibleId, ...rest } = router.query
      const params = collapsibleId === id.toString() ? rest : { ...rest, collapsibleId: id }
      router.replace(
        {
          query: params,
        },
        undefined,
        { shallow: true },
      )
    } else {
      setShowItem((prev) => (prev === id ? null : id))
    }
  }

  return (
    <ul className={cx(styles.List, additionalClasses)}>
      {items.map((item, idx) => {
        const isOpen = useQueryParams ? idx === Number(router.query.collapsibleId) : idx === showItem
        return (
          <li className={styles.Container} key={item.title}>
            <div className={styles.Button}>
              <div className={styles.Content}>
                <button
                  onClick={(e) => toggleItemHandler(e, idx)}
                  className={cx(styles.Question, questionClassName, {
                    [styles.AlignLeft]: alignLeft,
                    [styles.QuestionOpen]: isOpen,
                  })}
                >
                  {item.title}
                </button>
                <div
                  className={cx(styles.Answer, answerClassName, {
                    [styles.AnswerDisplayed]: isOpen,
                    [styles.AlignLeft]: alignLeft,
                  })}
                >
                  {typeof item.content === 'string' ? <p>{item.content}</p> : item.content}
                </div>
              </div>
              <div className={styles.ArrowContainer} onClick={(e) => toggleItemHandler(e, idx)}>
                <Arrow direction={isOpen ? 'Up' : 'Down'} className={styles.Arrow} />
              </div>
            </div>
          </li>
        )
      })}
    </ul>
  )
}
